import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SignupModal from '../components/SignupModal';
import { Check, X, Sparkles, Zap, Shield, TrendingUp, Database, Headphones, Star, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const Pricing = () => {
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [activeTab, setActiveTab] = useState('agencies');

  const plans = [
    {
      name: 'Starter',
      price: '$80',
      period: 'month',
      description: 'Perfect for Small Teams, Startups, and Growing Businesses',
      features: [
        '500 leads/month',
        'Maximum of 3 campaigns',
        'Lead Dashboard',
        'Data Download',
        'Data Validation/Suppression/TAL',
        'Reject management',
        'Vendor bill management',
        'Email Support',
      ],
      popular: false,
      color: 'from-blue-500 to-blue-600',
    },
    {
      name: 'Professional',
      price: '$710',
      period: 'month',
      description: 'Perfect for Growing Businesses Ready to Scale Their Operations',
      features: [
        'All features from Starter',
        '5,000 leads/month',
        'Maximum of 30 campaigns',
        'Report/Format integration',
        'Auto Reporting',
        'Weekly financial reports',
        'Voice Log Portal',
        'Chat Support',
      ],
      popular: true,
      color: 'from-orange-500 to-red-600',
    },
    {
      name: 'Expert',
      price: '$1,340',
      period: 'month',
      description: 'Comprehensive and Scalable Solutions for Growing Large Organizations',
      features: [
        'All features from Professional',
        'Up to 10,000 leads/month',
        '30 campaigns',
        'API integration for custom workflows',
        'Dedicated account manager',
        'Campaign Analysis Report',
        'Priority support',
      ],
      popular: false,
      color: 'from-purple-500 to-purple-600',
    },
  ];

  const addons = [
    { name: 'Additional campaigns', price: 'Contact for pricing', icon: TrendingUp },
    { name: 'Extra leads (per 1,000)', price: 'Custom pricing', icon: Database },
    { name: 'Premium support upgrade', price: 'Contact sales', icon: Headphones },
    { name: 'Custom API integrations', price: 'Custom quote', icon: Zap },
  ];

  const comparisonFeatures = [
    { name: 'Leads/month', starter: '500', professional: '5,000', enterprise: '10,000' },
    { name: 'Max Campaigns', starter: '3', professional: '30', enterprise: '30' },
    { name: 'Analytical Dashboard', starter: true, professional: true, enterprise: true },
    { name: 'Data Export', starter: true, professional: true, enterprise: true },
    { name: 'Data Validation/Suppression/TAL', starter: true, professional: true, enterprise: true },
    { name: 'Lead Audit', starter: true, professional: true, enterprise: true },
    { name: 'Vendor Bill Management', starter: true, professional: true, enterprise: true },
    { name: 'Asset Creative Approval', starter: true, professional: true, enterprise: true },
    { name: 'Email Support', starter: true, professional: true, enterprise: true },
    { name: 'Platform API', starter: true, professional: true, enterprise: true },
    { name: 'Lead Template Integration', starter: false, professional: true, enterprise: true },
    { name: 'Auto Reporting', starter: false, professional: true, enterprise: true },
    { name: 'Weekly Financial Reports', starter: false, professional: true, enterprise: true },
    { name: 'Voice Log Portal', starter: false, professional: true, enterprise: true },
    { name: 'Chat Support', starter: false, professional: true, enterprise: true },
    { name: 'Third Party API Integration', starter: false, professional: false, enterprise: true },
    { name: 'Dedicated Account Manager', starter: false, professional: false, enterprise: true },
    { name: 'Campaign Analysis Report', starter: false, professional: false, enterprise: true },
    { name: 'Priority Support', starter: false, professional: false, enterprise: true },
  ];

  const partnerFeatures = [
    { name: 'Bulk Lead Upload', included: true },
    { name: 'Asset Creative Upload', included: true },
    { name: 'Dashboard', included: true },
    { name: 'Lead Validation', included: true },
    { name: 'Bill Management', included: true },
    { name: 'Email Support', included: true },
    { name: 'Voice Log Portal', included: true },
    { name: 'Chat Support', included: true },
    { name: 'Priority Support', included: true },
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Efficiency',
      description: 'Automate repetitive tasks like lead validation, enrichment, and scoring, freeing up your team to focus on high-value activities.',
    },
    {
      icon: Database,
      title: 'Data Accuracy',
      description: 'With real-time validation and data enrichment, you can rest assured that your team is always working with clean, reliable lead data.',
    },
    {
      icon: TrendingUp,
      title: 'Scalable Solution',
      description: 'As your lead volume grows, AALIGN grows with you, offering the flexibility and scalability you need to handle more leads without added complexity.',
    },
    {
      icon: Shield,
      title: 'Seamless Integration',
      description: 'With real-time validation and data enrichment, you can rest assured that your team is always working with clean, reliable lead data.',
    },
    {
      icon: TrendingUp,
      title: 'Real-Time Performance Tracking',
      description: 'Get immediate insights into how your campaigns are performing, allowing you to optimize your lead generation efforts on the fly.',
    },
    {
      icon: Shield,
      title: 'Compliance Assurance',
      description: 'AALIGN helps you stay compliant with data protection regulations like GDPR by automating lead validation, encryption, and audit trails, reducing compliance risks.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <Header />

      {/* Hero Section - Enhanced */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-orange-50 via-white to-red-50 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-200 rounded-full opacity-20 blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-200 rounded-full opacity-20 blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 px-6 py-3 rounded-full text-sm font-bold mb-8 shadow-sm animate-bounce-slow">
            <Sparkles className="w-4 h-4" />
            Simple, Transparent Pricing
          </div>
          <h1 className="text-5xl lg:text-7xl font-black mb-6 leading-tight">
            Plans That Grow
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 via-red-500 to-red-600 animate-gradient">
              With Your Business
            </span>
          </h1>
          <p className="text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto mb-8">
            Start free, scale as you grow. No hidden fees, no surprises.
          </p>
          
          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" />
              <span>30-day free trial</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards - Enhanced */}
      <section className="py-20 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-6">
            {plans.map((plan, idx) => (
              <div
                key={idx}
                className={`relative bg-white rounded-3xl overflow-hidden transition-all duration-500 hover:scale-105 ${
                  plan.popular
                    ? 'shadow-2xl border-4 border-orange-500 lg:-translate-y-4'
                    : 'shadow-lg border-2 border-gray-100 hover:border-orange-300'
                }`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-orange-500 to-red-600 text-white text-center py-3 font-bold text-sm flex items-center justify-center gap-2">
                    <Star className="w-4 h-4 fill-current" />
                    MOST POPULAR
                  </div>
                )}

                <div className={`p-8 ${plan.popular ? 'pt-16' : 'pt-8'}`}>
                  {/* Plan Header */}
                  <div className="mb-6">
                    <h3 className="text-3xl font-black mb-3">{plan.name}</h3>
                    <p className="text-gray-600 min-h-[60px] leading-relaxed">{plan.description}</p>
                  </div>

                  {/* Price */}
                  <div className="mb-8">
                    <div className="flex items-baseline gap-2">
                      <span className="text-6xl font-black bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                        {plan.price}
                      </span>
                      <span className="text-xl text-gray-500 font-medium">/{plan.period}</span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => setIsSignupModalOpen(true)}
                    className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 mb-8 flex items-center justify-center gap-2 group ${
                      plan.popular
                        ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg hover:shadow-xl hover:scale-105'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    Start Free Trial
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>

                  {/* Features */}
                  <div className="space-y-4">
                    <div className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">
                      What's Included
                    </div>
                    {plan.features.map((feature, featureIdx) => (
                      <div key={featureIdx} className="flex items-start gap-3 group">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Check className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-gray-700 leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Money-back Guarantee */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 bg-green-50 border-2 border-green-200 rounded-full px-6 py-3">
              <Shield className="w-5 h-5 text-green-600" />
              <span className="text-green-800 font-semibold">30-day money-back guarantee on all plans</span>
            </div>
          </div>
        </div>
      </section>

      {/* Add-ons Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-black mb-4">Add-ons & Extras</h2>
            <p className="text-lg text-gray-500">Customize your plan with additional features</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {addons.map((addon, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl px-8 py-6 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between"
              >
                <span className="font-semibold text-gray-900 text-lg">{addon.name}</span>
                <span className="text-orange-500 font-medium italic">{addon.price}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison Table - Enhanced */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-black mb-4">Compare All Features</h2>
            <p className="text-xl text-gray-600">See exactly what's included in each plan</p>
          </div>

          {/* Tabs */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setActiveTab('agencies')}
                className={`px-8 py-3 rounded-lg font-bold text-lg transition-all duration-300 ${
                  activeTab === 'agencies'
                    ? 'bg-white text-orange-600 shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Agencies
              </button>
              <button
                onClick={() => setActiveTab('partner')}
                className={`px-8 py-3 rounded-lg font-bold text-lg transition-all duration-300 ${
                  activeTab === 'partner'
                    ? 'bg-white text-orange-600 shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Partner
              </button>
            </div>
          </div>

          {/* Agencies Table */}
          {activeTab === 'agencies' && (
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <th className="px-6 py-5 text-left font-black text-gray-900 text-lg sticky left-0 bg-gradient-to-r from-gray-50 to-gray-100">
                        Features
                      </th>
                      <th className="px-6 py-5 text-center font-black text-gray-900">Starter</th>
                      <th className="px-6 py-5 text-center font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">
                        Professional
                      </th>
                      <th className="px-6 py-5 text-center font-black text-gray-900">Expert</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonFeatures.map((feature, idx) => (
                      <tr
                        key={idx}
                        className={`border-t border-gray-100 hover:bg-orange-50 transition-colors ${
                          idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }`}
                      >
                        <td className="px-6 py-4 font-semibold text-gray-900 sticky left-0 bg-inherit">
                          {feature.name}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {typeof feature.starter === 'boolean' ? (
                            feature.starter ? (
                              <div className="flex justify-center">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                  <Check className="w-5 h-5 text-green-600" />
                                </div>
                              </div>
                            ) : (
                              <div className="flex justify-center">
                                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                  <X className="w-5 h-5 text-gray-400" />
                                </div>
                              </div>
                            )
                          ) : (
                            <span className="font-bold text-gray-700">{feature.starter}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center bg-orange-50">
                          {typeof feature.professional === 'boolean' ? (
                            feature.professional ? (
                              <div className="flex justify-center">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                  <Check className="w-5 h-5 text-green-600" />
                                </div>
                              </div>
                            ) : (
                              <div className="flex justify-center">
                                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                  <X className="w-5 h-5 text-gray-400" />
                                </div>
                              </div>
                            )
                          ) : (
                            <span className="font-bold text-orange-600">{feature.professional}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {typeof feature.enterprise === 'boolean' ? (
                            feature.enterprise ? (
                              <div className="flex justify-center">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                  <Check className="w-5 h-5 text-green-600" />
                                </div>
                              </div>
                            ) : (
                              <div className="flex justify-center">
                                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                  <X className="w-5 h-5 text-gray-400" />
                                </div>
                              </div>
                            )
                          ) : (
                            <span className="font-bold text-gray-700">{feature.enterprise}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Partner Table */}
          {activeTab === 'partner' && (
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200 max-w-2xl mx-auto">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <th className="px-6 py-5 text-left font-black text-gray-900 text-lg">
                        Features
                      </th>
                      <th className="px-6 py-5 text-center font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">
                        Partner
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {partnerFeatures.map((feature, idx) => (
                      <tr
                        key={idx}
                        className={`border-t border-gray-100 hover:bg-orange-50 transition-colors ${
                          idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }`}
                      >
                        <td className="px-6 py-4 font-semibold text-gray-900">
                          {feature.name}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <Check className="w-5 h-5 text-green-600" />
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Why Choose AALIGN - Enhanced */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-4">Why Choose AALIGN?</h2>
            <p className="text-xl text-gray-300">The benefits that set us apart</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={idx}
                  className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-8 hover:border-orange-500 hover:shadow-2xl transition-all duration-300 group"
                >
                  <div className="w-14 h-14 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 group-hover:text-orange-400 transition-colors">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section - Enhanced */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl lg:text-5xl font-black mb-16 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[
              {
                q: 'Can I change plans later?',
                a: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.',
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit cards, PayPal, and wire transfers for Enterprise plans.',
              },
              {
                q: 'Is there a free trial?',
                a: 'Yes! All plans come with a 30-day free trial. No credit card required to start.',
              },
            ].map((faq, idx) => (
              <div
                key={idx}
                className="p-8 bg-white rounded-2xl shadow-lg border-2 border-gray-100 hover:border-orange-300 hover:shadow-xl transition-all duration-300"
              >
                <h3 className="font-bold text-xl mb-3 text-gray-900">{faq.q}</h3>
                <p className="text-gray-600 leading-relaxed text-lg">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Enhanced */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-r from-orange-500 via-red-500 to-red-600 rounded-3xl p-12 lg:p-16 text-center text-white shadow-2xl relative overflow-hidden">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-10 rounded-full translate-y-24 -translate-x-24"></div>

            <div className="relative z-10">
              <h2 className="text-4xl lg:text-5xl font-black mb-4">Ready to Get Started?</h2>
              <p className="text-xl lg:text-2xl text-orange-100 mb-10">
                Join 300+ businesses already growing with AALIGN.
              </p>
              <button
                onClick={() => setIsSignupModalOpen(true)}
                className="bg-white text-orange-600 hover:bg-gray-100 font-bold px-12 py-5 rounded-xl shadow-2xl transition-all transform hover:scale-105 text-lg inline-flex items-center gap-3 group"
              >
                Start Your Free Trial
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="mt-6 text-orange-100 text-sm">
                No credit card required • Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Signup Modal */}
      <SignupModal isOpen={isSignupModalOpen} onClose={() => setIsSignupModalOpen(false)} />
    </div>
  );
};

export default Pricing;
